<template>
  <div class="custom-collab-summary-tile">
    <div class="custom-collab-summary-tile__col-left">
      <!-- Creator info and messages -->
      <div class="creator-info-and-messages custom-collab-summary-tile__creator-info-and-messages">
        <div class="creator-info-and-messages__avatar-wrap">
          <RouterLink class="creator-info-and-messages__msg-counter-badge-wrap" :to="routerLinkTo">
            <MsgCountBadge class="creator-info-and-messages__msg-counter-badge" :count="props.customCollabWidgetPayload.unreadMessageCount" :isHoverable="true" />
          </RouterLink>
          <AvatarWithFallback class="creator-info-and-messages__avatar" :src="props.customCollabWidgetPayload.entityPhotoUrl" :objectFit="props.userType === 'creator' ? 'contain' : 'cover'" />
        </div>

        <div class="creator-info-and-messages__name">
          {{ props.customCollabWidgetPayload.entityName }}
        </div>
      </div>
      <!-- / Creator info and messages -->
    </div>

    <div class="custom-collab-summary-tile__col-right">
      <!-- Custom collab badge -->
      <div class="custom-collab-badge custom-collab-summary-tile__custom-collab-badge">
        <IconEmbedded class="custom-collab-badge__icon" name="handshake_2" :size="20" color="rgba(0, 0, 0, 0.5)" />
        <div class="custom-collab-badge__text">Custom Collab</div>
      </div>
      <!-- / Custom collab badge -->

      <!-- Main info -->
      <div class="main-info custom-collab-summary-tile__main-info">
        <div class="main-info__col-left">
          <RouterLink class="global-h4 main-info__name" :to="routerLinkTo">
            {{ props.customCollabWidgetPayload.title }}
          </RouterLink>
          <div class="main-info__date-and-status">
            <div v-if="props.customCollabWidgetPayload.dueDate || props.customCollabWidgetPayload.activityDate" class="main-info__date">
              <IconEmbedded class="main-info__date-icon" name="calendar_2" :size="18" color="rgba(0, 0, 0, 0.5)" />
              <span v-if="props.customCollabWidgetPayload.activityDate" style="margin-right: 7px">
                <b>Activity:</b>&nbsp;{{ moment(props.customCollabWidgetPayload.activityDate).format("MMM Do YYYY") }}
              </span>
              <span v-if="props.customCollabWidgetPayload.dueDate"> <b>Due:</b>&nbsp;{{ moment(props.customCollabWidgetPayload.dueDate).format("MMM Do YYYY") }} </span>
            </div>
            <CollabStatusStripe
              class="main-info__status-snippet"
              :text="props.customCollabWidgetPayload.statusText"
              :color="[CustomCollabStatuses.DeliverablesReady].includes(props.customCollabWidgetPayload.statusCode) ? 'green' : 'white'"
              :isWithCheckIcon="[CustomCollabStatuses.DeliverablesReady].includes(props.customCollabWidgetPayload.statusCode)"
            />
          </div>
        </div>

        <div class="main-info__col-right">
          <RouterLink class="main-info__circle-cta-button" :to="routerLinkTo">
            <IconEmbedded name="caret-right_3" color="rgba(5, 133, 135, 0.5)" />
          </RouterLink>
        </div>
      </div>
      <!-- / Main info -->

      <div class="custom-collab-summary-tile__additional-info">
        <!-- Last message -->
        <div v-if="props.customCollabWidgetPayload.lastMessageText" class="last-message custom-collab-summary-tile__last-message">
          <div class="last-message__date">
            {{ moment(props.customCollabWidgetPayload.lastMessageDate).fromNow() }}
          </div>
          <div class="last-message__text">
            {{ props.customCollabWidgetPayload.lastMessageText }}
          </div>
        </div>
        <!-- / Last message -->

        <!-- Creator notes -->
        <div v-if="props.customCollabWidgetPayload.creatorNotes" class="creator-notes custom-collab-summary-tile__creator-notes">
          <div class="creator-notes__header">
            <IconEmbedded class="creator-notes__icon" name="file-text_2-5" :size="15" color="rgba(0, 0, 0, 0.5)" />
            <div class="global-h6 creator-notes__title">Creator Notes</div>
          </div>

          <div class="creator-notes__text">
            {{ props.customCollabWidgetPayload.creatorNotes }}
          </div>
        </div>
        <!-- / Creator notes -->
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";
import moment from "moment";

// Components
import AvatarWithFallback from "@components/AvatarWithFallback.vue";
import MsgCountBadge from "@components/MsgCountBadge.vue";
import CollabStatusStripe from "@components/CollabStatusStripe.vue";
import IconEmbedded from "@components/ui/IconEmbedded.vue";

// Types
import { CustomCollabWidgetPayload, CustomCollabStatuses } from "@contracts/customCollabs";

// Global variables & injections
const contentBaseUri = globalThis.Bootstrap.Config.contentCdnBaseUri;

const props = withDefaults(
  defineProps<{
    customCollabWidgetPayload: CustomCollabWidgetPayload | null;
    userType: "customer" | "creator";
  }>(),
  {
    customCollabWidgetPayload: null,
    userType: "customer",
  }
);

// Generate routerLink link TO object =========================================
const routerLinkTo = computed<object>(() => {
  if (props.userType === "customer") {
    return {
      name: "CustomerCustomCollabDetails",
      params: {
        creatorId: props.customCollabWidgetPayload.creatorId,
        pageId: props.customCollabWidgetPayload.communityId,
        customCollabId: props.customCollabWidgetPayload.customCollabId,
      },
    };
  } else if (props.userType === "creator") {
    return {
      name: "CreatorCustomCollabDetails",
      params: {
        creatorId: props.customCollabWidgetPayload.creatorId,
        communityId: props.customCollabWidgetPayload.communityId,
        customCollabId: props.customCollabWidgetPayload.customCollabId,
      },
    };
  }
});
</script>

<style scoped lang="scss">
@import "@/scss/screen-size-ranges.scss";
@import "@/scss/variables.scss";

// Creator info and messages ==================================================
.creator-info-and-messages {
  display: flex;
  flex-direction: column;
  align-items: center;

  &__avatar-wrap {
    width: 60px;
    margin-bottom: 3px;
    position: relative;
    z-index: 0;
  }

  &__avatar {
    width: 100%;
  }

  &__msg-counter-badge-wrap {
    position: absolute;
    inset: 0 -7px auto auto;
    z-index: 1;
  }

  &__msg-counter-badge {
  }

  &__name {
    text-align: center;
    line-height: 15px;
  }
}

// Custom collab badge ========================================================
.custom-collab-badge {
  height: 20px;
  padding: 0 11px 2px 4px;
  box-sizing: border-box;
  border: 1px rgb(193, 193, 193, 1) solid;
  border-radius: 100px;
  display: flex;
  align-items: center;
  background: rgb(243, 243, 243, 1);

  &__icon {
    margin-right: 2px;
  }

  &__text {
    color: rgba(0, 0, 0, 1);
    font:
      600 12px/12px "Quicksand",
      sans-serif;
  }
}

// Main info ==================================================================
.main-info {
  display: flex;
  gap: 0 10px;

  // Col left -------------------------
  &__col-left {
    display: flex;
    flex-wrap: wrap;
  }

  &__name {
    width: 100%;
    margin-bottom: 10px;
    line-height: 20px;
    text-decoration: underline;
    text-decoration-color: rgba(0, 0, 0, 0.2);
    text-underline-offset: 3px;
    text-decoration-thickness: 1px;
    text-decoration-style: solid;
    cursor: pointer;
    user-select: none;

    &:hover {
      text-decoration: none;
    }
  }

  &__date-and-status {
    gap: 8px 16px;
    display: flex;
    flex-wrap: wrap;
    align-items: center;
  }

  &__date {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    white-space: nowrap;
  }

  &__date-icon {
    margin-right: 4px;
  }

  &__status-snippet {
  }

  // Col right ------------------------
  &__col-right {
  }

  &__circle-cta-button {
    width: 40px;
    height: 40px;
    padding-left: 2px;
    box-sizing: border-box;
    display: flex;
    justify-content: center;
    align-items: center;
    position: relative;
    z-index: 0;
    cursor: pointer;

    &::before {
      content: "";
      width: 100%;
      height: 100%;
      box-sizing: border-box;
      border: 1px rgb(5, 133, 135, 1) solid;
      border-radius: 100px;
      position: absolute;
      inset: 0 auto auto 0;
      background: rgba(255, 255, 255, 1);
      transition:
        width 0.05s ease-in-out,
        height 0.05s ease-in-out,
        inset 0.05s ease-in-out;
    }

    &:hover::before {
      width: calc(100% + 6px);
      height: calc(100% + 6px);
      inset: -3px auto auto -3px;
    }
  }
}

// Last message ===============================================================
.last-message {
  padding: 10px 19px 12px;
  border-radius: 0 6px 6px 6px;
  position: relative;
  z-index: 0;
  background: rgba(255, 255, 255, 1);

  &::before {
    content: "";
    width: 15px;
    height: 17px;
    position: absolute;
    inset: 0 auto auto -15px;
    background: rgba(255, 255, 255, 1);
    clip-path: polygon(0 0, 100% 0, 100% 100%, 0 0);
  }

  &__date {
    margin-bottom: 3px;
    color: rgba(0, 0, 0, 0.5);
  }

  &__text {
    display: -webkit-box !important;
    overflow: hidden !important;
    line-clamp: 3 !important;
    -webkit-line-clamp: 3 !important;
    text-overflow: ellipsis !important;
    -webkit-box-orient: vertical !important;
  }
}

// Creator notes ==============================================================
.creator-notes {
  padding: 12px 19px 15px;
  border-radius: 6px;
  background: rgba(255, 255, 255, 1);

  &__header {
    margin-bottom: 4px;
    display: flex;
    align-items: center;
  }

  &__icon {
    margin-right: 4px;
  }

  &__title {
  }

  &__text {
    display: -webkit-box !important;
    overflow: hidden !important;
    line-clamp: 3 !important;
    -webkit-line-clamp: 3 !important;
    text-overflow: ellipsis !important;
    -webkit-box-orient: vertical !important;
  }
}

// Custom collab summary tile =================================================
.custom-collab-summary-tile {
  @include standardFont;
  border: 1px rgba(0, 0, 0, 0.15) solid;
  border-radius: 6px;
  display: flex;

  // Col left -------------------------
  &__col-left {
    width: 120px;
    min-width: 120px;
    height: 100%;
    padding: 19px 0;
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    align-items: center;
  }

  &__creator-info-and-messages {
    max-width: calc(100% - 14px);
  }

  // Col right ------------------------
  &__col-right {
    flex-grow: 1;
    height: 100%;
    padding: 22px 25px;
    box-sizing: border-box;
    border-radius: 0 5px 5px 0;
    position: relative;
    z-index: 0;
    background: rgba(243, 243, 243, 1);
  }

  &__custom-collab-badge {
    position: absolute;
    inset: -10px auto auto 23px;
  }

  &__main-info {
    margin-bottom: 14px;
  }

  &__additional-info {
  }

  &__last-message {
  }

  &__creator-notes {
  }

  &__deliverables-masonry {
  }
}
</style>
